/**
 * ContactsInput Component
 *
 * Tag-style input for managing client contact emails.
 * Includes email validation before adding.
 */

import { useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ContactsInputProps {
  value: string[];
  onChange: (contacts: string[]) => void;
  placeholder?: string;
  maxContacts?: number;
  disabled?: boolean;
  className?: string;
}

// Simple email validation regex
const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export function ContactsInput({
  value,
  onChange,
  placeholder = 'Add email and press Enter',
  maxContacts = 20,
  disabled = false,
  className,
}: ContactsInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const trimmed = inputValue.trim().toLowerCase();

        if (!trimmed) return;

        // Validate email format
        if (!isValidEmail(trimmed)) {
          setValidationError('Invalid email format');
          return;
        }

        // Check for duplicates
        if (value.includes(trimmed)) {
          setValidationError('Email already added');
          return;
        }

        // Check max limit
        if (value.length >= maxContacts) {
          setValidationError('Maximum contacts reached');
          return;
        }

        onChange([...value, trimmed]);
        setInputValue('');
        setValidationError(null);
      } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
        // Remove last contact when backspace is pressed on empty input
        onChange(value.slice(0, -1));
        setValidationError(null);
      }
    },
    [inputValue, value, onChange, maxContacts],
  );

  const handleRemove = useCallback(
    (contact: string) => {
      onChange(value.filter((c) => c !== contact));
    },
    [value, onChange],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      if (validationError) setValidationError(null);
    },
    [validationError],
  );

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap gap-1.5">
        {value.map((contact) => (
          <Badge key={contact} variant="secondary" className="gap-1 pr-1">
            {contact}
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemove(contact)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
                aria-label={`Remove ${contact}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
      <Input
        type="email"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={
          value.length >= maxContacts ? 'Maximum contacts reached' : placeholder
        }
        disabled={disabled || value.length >= maxContacts}
        className={cn('h-9', validationError && 'border-destructive')}
      />
      {validationError && (
        <p className="text-xs text-destructive">{validationError}</p>
      )}
      {!validationError && value.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {value.length} contact{value.length !== 1 ? 's' : ''} added
        </p>
      )}
    </div>
  );
}
